/**
 * Baby Growth Service
 *
 * Provides CRUD operations for baby growth entries (weight, height, head circumference)
 * with support for WHO percentile calculations.
 */

import { supabase } from '@/lib/supabase';

// Types
export interface BabyGrowthEntry {
  id: string;
  pregnancy_id: string;
  baby_index: number;
  measured_at: string;
  weight_grams: number | null;
  height_cm: number | null;
  head_circumference_cm: number | null;
  measured_by: string | null;
  measured_by_name?: string;
  notes: string | null;
  created_at: string;
}

export interface CreateBabyGrowthEntry {
  pregnancy_id: string;
  baby_index?: number;
  measured_at: string;
  weight_grams?: number | null;
  height_cm?: number | null;
  head_circumference_cm?: number | null;
  notes?: string | null;
}

export interface UpdateBabyGrowthEntry {
  measured_at?: string;
  weight_grams?: number | null;
  height_cm?: number | null;
  head_circumference_cm?: number | null;
  notes?: string | null;
}

/**
 * Get all growth entries for a pregnancy/baby
 */
export async function getBabyGrowthEntries(
  pregnancyId: string,
  babyIndex: number = 0
): Promise<{ data: BabyGrowthEntry[]; error: string | null }> {
  const { data, error } = await supabase
    .rpc('get_baby_growth_entries', {
      p_pregnancy_id: pregnancyId,
      p_baby_index: babyIndex,
    });

  if (error) {
    console.error('Error fetching baby growth entries:', error);
    return { data: [], error: error.message };
  }

  return { data: data || [], error: null };
}

/**
 * Get the latest growth entry for a baby
 */
export async function getLatestBabyGrowth(
  pregnancyId: string,
  babyIndex: number = 0
): Promise<{ data: BabyGrowthEntry | null; error: string | null }> {
  const { data, error } = await supabase
    .rpc('get_latest_baby_growth', {
      p_pregnancy_id: pregnancyId,
      p_baby_index: babyIndex,
    });

  if (error) {
    console.error('Error fetching latest baby growth:', error);
    return { data: null, error: error.message };
  }

  return { data: data?.[0] || null, error: null };
}

/**
 * Count growth entries for a pregnancy
 */
export async function countBabyGrowthEntries(
  pregnancyId: string,
  babyIndex?: number
): Promise<{ count: number; error: string | null }> {
  const { data, error } = await supabase
    .rpc('count_baby_growth_entries', {
      p_pregnancy_id: pregnancyId,
      p_baby_index: babyIndex ?? null,
    });

  if (error) {
    console.error('Error counting baby growth entries:', error);
    return { count: 0, error: error.message };
  }

  return { count: data || 0, error: null };
}

/**
 * Create a new growth entry
 */
export async function addBabyGrowthEntry(
  userId: string,
  entry: CreateBabyGrowthEntry
): Promise<{ data: BabyGrowthEntry | null; error: string | null }> {
  const { data, error } = await supabase
    .from('baby_growth')
    .insert({
      pregnancy_id: entry.pregnancy_id,
      baby_index: entry.baby_index ?? 0,
      measured_at: entry.measured_at,
      weight_grams: entry.weight_grams ?? null,
      height_cm: entry.height_cm ?? null,
      head_circumference_cm: entry.head_circumference_cm ?? null,
      measured_by: userId,
      notes: entry.notes ?? null,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating baby growth entry:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

/**
 * Update a growth entry
 */
export async function updateBabyGrowthEntry(
  entryId: string,
  updates: UpdateBabyGrowthEntry
): Promise<{ data: BabyGrowthEntry | null; error: string | null }> {
  const { data, error } = await supabase
    .from('baby_growth')
    .update({
      ...updates,
    })
    .eq('id', entryId)
    .select()
    .single();

  if (error) {
    console.error('Error updating baby growth entry:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

/**
 * Delete a growth entry
 */
export async function deleteBabyGrowthEntry(
  entryId: string
): Promise<{ success: boolean; error: string | null }> {
  const { error } = await supabase
    .from('baby_growth')
    .delete()
    .eq('id', entryId);

  if (error) {
    console.error('Error deleting baby growth entry:', error);
    return { success: false, error: error.message };
  }

  return { success: true, error: null };
}

/**
 * Create initial birth entry from baby_data if it doesn't exist
 */
export async function createInitialGrowthEntry(
  pregnancyId: string
): Promise<{ data: BabyGrowthEntry[] | null; error: string | null }> {
  const { data, error } = await supabase
    .rpc('create_initial_growth_entry', {
      p_pregnancy_id: pregnancyId,
    });

  if (error) {
    console.error('Error creating initial growth entry:', error);
    return { data: null, error: error.message };
  }

  return { data: data || [], error: null };
}

/**
 * Get a single growth entry by ID
 */
export async function getBabyGrowthEntry(
  entryId: string
): Promise<{ data: BabyGrowthEntry | null; error: string | null }> {
  const { data, error } = await supabase
    .from('baby_growth')
    .select('*')
    .eq('id', entryId)
    .single();

  if (error) {
    console.error('Error fetching baby growth entry:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}
